// store/integrations/types.ts

export interface InstallationSummaryDTO {
	id: string;
	name: string;
	status: string;
	config?: any;       // optional, if you need it in the UI
}

export interface AppDTO {
	id: string
	key: string
	name: string
	developer: string
	logo_url: string
	banner_url?: string
	category: any
	description: string
	active: boolean
	created_at: string
	updated_at: string
	latest_version_id: string
	version_metadata: {
		ui?: {
			allowMultipleInstalls?: boolean;
			requiresSetup?: boolean;
			setupUrl?: string;
		}
		authType?: "oauth" | "apikey" | "dns" |"none"
		authUrl?: string
		tokenUrl?: string
		[k: string]: any
	}
	installations: InstallationSummaryDTO[];
}

export interface AppVersionDTO {
	id: string
	app_id: string
	version: string
	metadata: any
	active: boolean
	created_at: string
	updated_at: string
}

export interface InstallationDTO {
	id: string
	name: string
	app_version_id: string
	tenant_id: string
	config_json: string
	installed_at: string
	status: string
	updated_at: string
	uninstalled_at: string | null
	version: any
}

export interface AccountDTO {
	account_id: string
	account_name: string
	access_token: string
	logo_url: string
}

export interface PageDTO {
	id: string
	name: string
	access_token: string
	logo_url: string
}

export interface FormDTO {
	id: string
	name: string
	questions: Array<{ key: string; label: string }>
}

// discovery metadata for each operation
export interface DiscoveryOpMeta {
	path: string;
	method: string;
	idField: string;
	labelField: string;
	resultsPath?: string;
	tokenField?: string;
	tokenParam?: string;
	paramsFrom?: Record<string, string>;
	[k: string]: any;
}

// full metadata JSON shape
export interface Metadata {
	setupSteps: Step[];
	discovery: Record<string, DiscoveryOpMeta>;
	endpoints: {
		baseUrl: string;
		[k: string]: any;
	};
	[k: string]: any;
}

 export interface WidgetConfig {
	   widget: "cardPicker"|"dropdown"|"fieldMapper" | "variableMapper" |"textInput";
	   discoveryOp?: string;
	   idField?: string;
	   labelField?: string;
	   logoField?: string;
	   paramsFrom?: Record<string,string>;
	   mappingSource?: { type: string; modelKey: string, options?: any };
	   field?: string;           // for textInput
	   placeholder?: string;     // for textInput
	   allowCustom?: boolean;
	   renderTemplate?: any
}

 export interface Step {
	   id: string;
	   type: "discovery" | "mapping";
	   title: string;
	   description: string;
	   widgets: WidgetConfig[];   // ← replaced `ui` with an array of `WidgetConfig`
	   postAction?: {
	     type?: string;
	     minItemsRequired?: number;
	     [key: string]: any;
	   };
	 }

export interface FieldDTO { key: string; label: string }
export interface FieldMappingItem {
	form_field: FieldDTO;
	crm_field: FieldDTO | null;
}

 export interface MappingRecord {
	   id: string;
	   name: string;
	   installation_id: string;
	   config: Record<string, any>;
	   created_at: string;
	   updated_at?: string;
	 }

	 export interface DiscoveryData {
		records: any[];
		template?: any;
	  }
export interface IntegrationState {
	// APPS list
	apps: AppDTO[]
	loadingApps: boolean
	errorApps?: string

	// INSTALLATIONS list
	installations: InstallationDTO[]
	loadingInstalls: boolean
	errorInstalls?: string

	// DETAIL ap
	currentApp: AppDTO | null
	loadingDetail: boolean
	errorDetail?: string

	// VERSIONS by app
	versionsByAppId: Record<string, AppVersionDTO[]>
	loadingVersions: boolean
	errorVersions?: string

	// COMPLETE OAuth
	loadingOAuthComplete: boolean;
	errorOAuthComplete?: string;
	oauthCompleteData?: { provider: string; id: string };

	// WIZARD: PAGES & FORMS & FIELD
	metadata?: {
		setupSteps: Step[];   // you can refine Step type here if you like
		[k: string]: any;
	};
	loadingMetadata: boolean
	errorMetadata?: string

	accounts: AccountDTO[]
	loadingAccounts: boolean
	errorAccounts?: string

	pages: PageDTO[]
	loadingPages: boolean
	errorPages?: string

	forms: FormDTO[]
	loadingForms: boolean
	errorForms?: string

	fields: FieldDTO[]
	loadingFields: boolean
	errorFields?: string

	mapping: FieldMappingItem[]
	loadingMapping: boolean
	errorMapping?: string

	currentMappingRecord?: MappingRecord;
	loadingCurrentMappingRecord: boolean;
	errorCurrentMappingRecord?: string;

	mappingRecords: MappingRecord[];
	loadingMappingRecords: boolean;
	errorMappingRecords?: string;

	discovery: Record<string, DiscoveryData>;
	loadingDiscovery: Record<string, boolean>;
	errorDiscovery: Record<string, string | undefined>;

	loadingOperation: boolean;
	errorOperation?: string;
}

export enum IntegrationActionTypes {
	GET_APPS_REQUEST = "@@integrations/GET_APPS_REQUEST",
	GET_APPS_SUCCESS = "@@integrations/GET_APPS_SUCCESS",
	GET_APPS_ERROR = "@@integrations/GET_APPS_ERROR",

	GET_INSTALLS_REQUEST = "@@integrations/GET_INSTALLS_REQUEST",
	GET_INSTALLS_SUCCESS = "@@integrations/GET_INSTALLS_SUCCESS",
	GET_INSTALLS_ERROR = "@@integrations/GET_INSTALLS_ERROR",

	GET_APP_REQUEST = "@@integrations/GET_APP_REQUEST",
	GET_APP_SUCCESS = "@@integrations/GET_APP_SUCCESS",
	GET_APP_ERROR = "@@integrations/GET_APP_ERROR",

	GET_VERSIONS_REQUEST = "@@integrations/GET_VERSIONS_REQUEST",
	GET_VERSIONS_SUCCESS = "@@integrations/GET_VERSIONS_SUCCESS",
	GET_VERSIONS_ERROR = "@@integrations/GET_VERSIONS_ERROR",

	POST_INSTALL_REQUEST = "@@integrations/POST_INSTALL_REQUEST",
	POST_INSTALL_SUCCESS = "@@integrations/POST_INSTALL_SUCCESS",
	POST_INSTALL_ERROR = "@@integrations/POST_INSTALL_ERROR",

	DELETE_INSTALL_REQUEST = "@@integrations/DELETE_INSTALL_REQUEST",
	DELETE_INSTALL_SUCCESS = "@@integrations/DELETE_INSTALL_SUCCESS",
	DELETE_INSTALL_ERROR = "@@integrations/DELETE_INSTALL_ERROR",

	INIT_OAUTH_REQUEST = "@@integrations/INIT_OAUTH_REQUEST",
	INIT_OAUTH_SUCCESS = "@@integrations/INIT_OAUTH_SUCCESS",
	INIT_OAUTH_ERROR = "@@integrations/INIT_OAUTH_ERROR",

	COMPLETE_OAUTH_REQUEST = "@@integrations/COMPLETE_OAUTH_REQUEST",
	COMPLETE_OAUTH_SUCCESS = "@@integrations/COMPLETE_OAUTH_SUCCESS",
	COMPLETE_OAUTH_ERROR = "@@integrations/COMPLETE_OAUTH_ERROR",

	GET_METADATA_REQUEST = "@@integrations/GET_METADATA_REQUEST",
	GET_METADATA_SUCCESS = "@@integrations/GET_METADATA_SUCCESS",
	GET_METADATA_ERROR = "@@integrations/GET_METADATA_ERROR",

	GET_ACCOUNTS_REQUEST = "@@integrations/GET_ACCOUNTS_REQUEST",
	GET_ACCOUNTS_SUCCESS = "@@integrations/GET_ACCOUNTS_SUCCESS",
	GET_ACCOUNTS_ERROR = "@@integrations/GET_ACCOUNTS_ERROR",

	GET_PAGES_REQUEST = "@@integrations/GET_PAGES_REQUEST",
	GET_PAGES_SUCCESS = "@@integrations/GET_PAGES_SUCCESS",
	GET_PAGES_ERROR = "@@integrations/GET_PAGES_ERROR",

	GET_FORMS_REQUEST = "@@integrations/GET_FORMS_REQUEST",
	GET_FORMS_SUCCESS = "@@integrations/GET_FORMS_SUCCESS",
	GET_FORMS_ERROR = "@@integrations/GET_FORMS_ERROR",

	GET_FIELDS_REQUEST = "@@integrations/GET_FIELDS_REQUEST",
	GET_FIELDS_SUCCESS = "@@integrations/GET_FIELDS_SUCCESS",
	GET_FIELDS_ERROR = "@@integrations/GET_FIELDS_ERROR",

	GET_MAPPING_REQUEST = "@@integrations/GET_MAPPING_REQUEST",
	GET_MAPPING_SUCCESS = "@@integrations/GET_MAPPING_SUCCESS",
	GET_MAPPING_ERROR = "@@integrations/GET_MAPPING_ERROR",

	// discovery pipeline
	GET_DISCOVERY_REQUEST = "@@integrations/GET_DISCOVERY_REQUEST",
	GET_DISCOVERY_SUCCESS = "@@integrations/GET_DISCOVERY_SUCCESS",
	GET_DISCOVERY_ERROR = "@@integrations/GET_DISCOVERY_ERROR",

	SAVE_MAPPING_REQUEST = "@@integrations/SAVE_MAPPING_REQUEST",
	SAVE_MAPPING_SUCCESS = "@@integrations/SAVE_MAPPING_SUCCESS",
	SAVE_MAPPING_ERROR = "@@integrations/SAVE_MAPPING_ERROR",


	// mappings
	GET_MAPPING_RECORDS_REQUEST    = "@@integrations/GET_MAPPING_RECORDS_REQUEST",
	GET_MAPPING_RECORDS_SUCCESS    = "@@integrations/GET_MAPPING_RECORDS_SUCCESS",
	GET_MAPPING_RECORDS_ERROR      = "@@integrations/GET_MAPPING_RECORDS_ERROR",
  
	CREATE_MAPPING_RECORD_REQUEST  = "@@integrations/CREATE_MAPPING_RECORD_REQUEST",
	CREATE_MAPPING_RECORD_SUCCESS  = "@@integrations/CREATE_MAPPING_RECORD_SUCCESS",
	CREATE_MAPPING_RECORD_ERROR    = "@@integrations/CREATE_MAPPING_RECORD_ERROR",
  
	UPDATE_MAPPING_RECORD_REQUEST  = "@@integrations/UPDATE_MAPPING_RECORD_REQUEST",
	UPDATE_MAPPING_RECORD_SUCCESS  = "@@integrations/UPDATE_MAPPING_RECORD_SUCCESS",
	UPDATE_MAPPING_RECORD_ERROR    = "@@integrations/UPDATE_MAPPING_RECORD_ERROR",
  
	DELETE_MAPPING_RECORD_REQUEST  = "@@integrations/DELETE_MAPPING_RECORD_REQUEST",
	DELETE_MAPPING_RECORD_SUCCESS  = "@@integrations/DELETE_MAPPING_RECORD_SUCCESS",
	DELETE_MAPPING_RECORD_ERROR    = "@@integrations/DELETE_MAPPING_RECORD_ERROR",

	GET_MAPPING_RECORD_REQUEST   = "@@integrations/GET_MAPPING_RECORD_REQUEST",
	GET_MAPPING_RECORD_SUCCESS   = "@@integrations/GET_MAPPING_RECORD_SUCCESS",
	GET_MAPPING_RECORD_ERROR     = "@@integrations/GET_MAPPING_RECORD_ERROR",

	INVOKE_OPERATION_REQUEST = "@@integrations/INVOKE_OPERATION_REQUEST",
	INVOKE_OPERATION_SUCCESS = "@@integrations/INVOKE_OPERATION_SUCCESS",
	INVOKE_OPERATION_ERROR   = "@@integrations/INVOKE_OPERATION_ERROR",
}
